import { Vector3, length } from './vector3';
import { Quaternion, identity } from './quaternion';

export interface Matrix4x4 {
  m11: number;
  m12: number;
  m13: number;
  m14: number;
  m21: number;
  m22: number;
  m23: number;
  m24: number;
  m31: number;
  m32: number;
  m33: number;
  m34: number;
  m41: number;
  m42: number;
  m43: number;
  m44: number;
}

/**
 * Creates identity matrix
 */
export function createIdentityMatrix(): Matrix4x4 {
  return {
    m11: 1,
    m12: 0,
    m13: 0,
    m14: 0,
    m21: 0,
    m22: 1,
    m23: 0,
    m24: 0,
    m31: 0,
    m32: 0,
    m33: 1,
    m34: 0,
    m41: 0,
    m42: 0,
    m43: 0,
    m44: 1,
  };
}

/**
 * Get scale of the matrix
 */
export function getScale(matrix: Matrix4x4): Vector3 {
  const sx = length({ x: matrix.m11, y: matrix.m12, z: matrix.m13 });
  const sy = length({ x: matrix.m21, y: matrix.m22, z: matrix.m23 });
  const sz = length({ x: matrix.m31, y: matrix.m32, z: matrix.m33 });
  return { x: sx, y: sy, z: sz };
}

/**
 * Mutate matrix by applying scale to it
 * @param matrix Matrix
 * @param scale Scale
 */
export function setScale(matrix: Matrix4x4, scale: Vector3): void {
  const s = getScale(matrix);
  const sx = (1 / s.x) * scale.x;
  const sy = (1 / s.y) * scale.y;
  const sz = (1 / s.z) * scale.z;
  matrix.m11 *= sx;
  matrix.m12 *= sx;
  matrix.m13 *= sx;
  matrix.m21 *= sy;
  matrix.m22 *= sy;
  matrix.m23 *= sy;
  matrix.m31 *= sz;
  matrix.m32 *= sz;
  matrix.m33 *= sz;
}

/**
 * Get position of the matrix
 * @param matrix Matrix
 */
export function getPosition(matrix: Matrix4x4): Vector3 {
  return { x: matrix.m41, y: matrix.m42, z: matrix.m43 };
}

/**
 * Mutate matrix by applying position to it
 * @param matrix Matrix
 * @param position Position
 */
export function setPosition(matrix: Matrix4x4, position: Vector3): void {
  matrix.m41 = position.x;
  matrix.m42 = position.y;
  matrix.m43 = position.z;
}

/**
 * Get quaternion from matrix.
 * Pure rotation matrix is expected as input.
 * @param matrix Pure rotation matrix
 */
export function getRotation(matrix: Matrix4x4): Quaternion {
  // http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm
  // Sources from System.Numerics .NET 4.8 <=
  const trace = matrix.m11 + matrix.m22 + matrix.m33;
  const q: Quaternion = identity();

  if (trace > 0.0) {
    let s = Math.sqrt(trace + 1.0);
    q.w = s * 0.5;
    s = 0.5 / s;
    q.x = (matrix.m23 - matrix.m32) * s;
    q.y = (matrix.m31 - matrix.m13) * s;
    q.z = (matrix.m12 - matrix.m21) * s;
  } else {
    if (matrix.m11 >= matrix.m22 && matrix.m11 >= matrix.m33) {
      const s = Math.sqrt(1.0 + matrix.m11 - matrix.m22 - matrix.m33);
      const invS = 0.5 / s;
      q.x = 0.5 * s;
      q.y = (matrix.m12 + matrix.m21) * invS;
      q.z = (matrix.m13 + matrix.m31) * invS;
      q.w = (matrix.m23 - matrix.m32) * invS;
    } else if (matrix.m22 > matrix.m33) {
      const s = Math.sqrt(1.0 + matrix.m22 - matrix.m11 - matrix.m33);
      const invS = 0.5 / s;
      q.x = (matrix.m21 + matrix.m12) * invS;
      q.y = 0.5 * s;
      q.z = (matrix.m32 + matrix.m23) * invS;
      q.w = (matrix.m31 - matrix.m13) * invS;
    } else {
      const s = Math.sqrt(1.0 + matrix.m33 - matrix.m11 - matrix.m22);
      const invS = 0.5 / s;
      q.x = (matrix.m31 + matrix.m13) * invS;
      q.y = (matrix.m32 + matrix.m23) * invS;
      q.z = 0.5 * s;
      q.w = (matrix.m12 - matrix.m21) * invS;
    }
  }
  return q;
}

/**
 * Mutate the matrix by applying rotation into it
 * @param matrix Matrix
 * @param rotation Rotation
 */
export function setRotation(matrix: Matrix4x4, rotation: Quaternion): void {
  // Ported from https://github.com/mrdoob/three.js/blob/dev/src/math/Matrix4.js
  const scale = getScale(matrix);
  const x = rotation.x;
  const y = rotation.y;
  const z = rotation.z;
  const w = rotation.w;
  const x2 = x + x;
  const y2 = y + y;
  const z2 = z + z;
  const xx = x * x2;
  const xy = x * y2;
  const xz = x * z2;
  const yy = y * y2;
  const yz = y * z2;
  const zz = z * z2;
  const wx = w * x2;
  const wy = w * y2;
  const wz = w * z2;
  matrix.m11 = (1 - (yy + zz)) * scale.x;
  matrix.m12 = (xy + wz) * scale.x;
  matrix.m13 = (xz - wy) * scale.x;

  matrix.m21 = (xy - wz) * scale.y;
  matrix.m22 = (1 - (xx + zz)) * scale.y;
  matrix.m23 = (yz + wx) * scale.y;

  matrix.m31 = (xz + wy) * scale.z;
  matrix.m32 = (yz - wx) * scale.z;
  matrix.m33 = (1 - (xx + yy)) * scale.z;

  // last column
  matrix.m14 = 0;
  matrix.m24 = 0;
  matrix.m34 = 0;
  matrix.m44 = 1;
}

/**
 * Get pure rotation part of the matrix
 * @param matrix Matrix
 */
export function getRotationMatrix(matrix: Matrix4x4): Matrix4x4 {
  const scale = getScale(matrix);
  // scale the rotation part
  const invSx = 1 / scale.x;
  const invSy = 1 / scale.y;
  const invSz = 1 / scale.z;
  // if determine is negative, we need to invert one scale
  const det = getDeterminant(matrix);
  if (det < 0) {
    scale.x = -scale.x;
  }
  const rotationMatrix = copy(matrix); // struct copy
  rotationMatrix.m11 *= invSx;
  rotationMatrix.m12 *= invSx;
  rotationMatrix.m13 *= invSx;
  rotationMatrix.m21 *= invSy;
  rotationMatrix.m22 *= invSy;
  rotationMatrix.m23 *= invSy;
  rotationMatrix.m31 *= invSz;
  rotationMatrix.m32 *= invSz;
  rotationMatrix.m33 *= invSz;
  return rotationMatrix;
}

export function getDeterminant(matrix: Matrix4x4): number {
  if (isAffine(matrix)) {
    return getNormalizedAffineDeterminant(matrix);
  }
  const num1 = matrix.m13 * matrix.m24 - matrix.m23 * matrix.m14;
  const num2 = matrix.m13 * matrix.m34 - matrix.m33 * matrix.m14;
  const num3 = matrix.m13 * matrix.m44 - matrix.m43 * matrix.m14;
  const num4 = matrix.m23 * matrix.m34 - matrix.m33 * matrix.m24;
  const num5 = matrix.m23 * matrix.m44 - matrix.m43 * matrix.m24;
  const num6 = matrix.m33 * matrix.m44 - matrix.m43 * matrix.m34;
  return (
    matrix.m41 * (matrix.m22 * num2 - matrix.m32 * num1 - matrix.m12 * num4) +
    matrix.m31 * (matrix.m12 * num5 - matrix.m22 * num3 + matrix.m42 * num1) +
    matrix.m21 * (matrix.m32 * num3 - matrix.m42 * num2 - matrix.m12 * num6) +
    matrix.m11 * (matrix.m22 * num6 - matrix.m32 * num5 + matrix.m42 * num4)
  );
}

export function isAffine(matrix: Matrix4x4): boolean {
  return matrix.m14 === 0 && matrix.m24 === 0 && matrix.m34 === 0 && matrix.m44 === 1;
}

function getNormalizedAffineDeterminant(matrix: Matrix4x4): number {
  return (
    matrix.m31 * (matrix.m12 * matrix.m23 - matrix.m22 * matrix.m13) +
    matrix.m21 * (matrix.m32 * matrix.m13 - matrix.m12 * matrix.m33) +
    matrix.m11 * (matrix.m22 * matrix.m33 - matrix.m32 * matrix.m23)
  );
}

/**
 * Create a copy of a matrix
 * @param matrix Matrix
 */
function copy(matrix: Matrix4x4): Matrix4x4 {
  return {
    m11: matrix.m11,
    m12: matrix.m12,
    m13: matrix.m13,
    m14: matrix.m14,
    // Second row
    m21: matrix.m21,
    m22: matrix.m22,
    m23: matrix.m23,
    m24: matrix.m24,
    // Third row
    m31: matrix.m31,
    m32: matrix.m32,
    m33: matrix.m33,
    m34: matrix.m34,
    // Fourth row
    m41: matrix.m41,
    m42: matrix.m42,
    m43: matrix.m43,
    m44: matrix.m44,
  };
}
